import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  styled,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { guidGenerator } from "../../helpers";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const PREFIX = "GeolocMapList";

const classes = {
  geolocListContainer: `${PREFIX}-geolocListContainer`,
  arrowsContainer: `${PREFIX}-arrowsContainer`,
};

const GeolocMapListContainer = styled("div")(({ theme }) => ({
  [`& .${classes.geolocListContainer}`]: {
    overflowY: "auto",
    width: "450px",
    marginBottom: "20px",
    // height: "400px",
    [theme.breakpoints.down("md")]: {
      marginLeft: "0",
      marginBottom: "20px",
      height: "auto",
      width: "100dvw",
      // maxHeight: "450px",
    },
    "&::-webkit-scrollbar": {
      width: "7px",
      height: "7px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
  },

  [`& .${classes.arrowsContainer}`]: {
    display: "flex",
    flexDirection: "column",
    marginRight: "12px",
  },
}));

const GeolocMapList = ({
  geolocs,
  showAll,
  setShowAll,
  handlePositionFocus,
  width,
  save,
}) => {
  const [geolocsList, setGeolocsList] = useState(geolocs || []);
  const [openDialog, setOpenDialog] = useState(false);
  const [newMarker, setNewMarker] = useState({
    title: "",
    latitude: "",
    longitude: "",
    radius: 100,
    visible: false,
    free: false,
    controlDate: false,
    startDate: null,
    endDate: null,
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const { t } = useTranslation();

  useEffect(() => {
    setGeolocsList(geolocs || []);
  }, [geolocs]);

  const handleChangeMarkerTitle = (index, value) => {
    const newGeolocsList = [...geolocsList];
    newGeolocsList[index].title = value;
    setGeolocsList(newGeolocsList);
    handleSaveGeolocs(newGeolocsList);
  };

  const handleChangeMarkerRadius = (index, value) => {
    const newGeolocsList = [...geolocsList];
    newGeolocsList[index].radius = value;
    setGeolocsList(newGeolocsList);
    handleSaveGeolocs(newGeolocsList);
  };

  const handleCheckboxChange = (index, field) => {
    const newGeolocsList = [...geolocsList];
    if (field === "controlDate" && !newGeolocsList[index][field]) {
      newGeolocsList[index].startDate =
        newGeolocsList[index].startDate || dayjs();
      newGeolocsList[index].endDate =
        newGeolocsList[index].endDate || dayjs().add(7, "day");
    }
    newGeolocsList[index][field] = !newGeolocsList[index][field];
    setGeolocsList(newGeolocsList);
    handleSaveGeolocs(newGeolocsList);
  };

  const handleSaveGeolocs = (newGeolocs) => {
    const listToSave = newGeolocs || geolocsList;
    save(
      listToSave.map((item) => {
        return {
          lat: item.lat || (item.position && item.position.lat) || 0,
          long: item.long || (item.position && item.position.lng) || 0,
          type: "Point",
          title: item.title,
          guid: item.guid || guidGenerator(),
          radius: item.radius,
          visible: item.visible,
          free: item.free,
          controlDate: item.controlDate,
          startDate: item.startDate || null,
          endDate: item.endDate || null,
        };
      })
    );
  };

  const handleMarkerPosition = (index, order) => {
    if (
      (order === "up" && index === 0) ||
      (order === "down" && index === geolocsList.length - 1)
    )
      return;
    let tmpGeolocs = [...geolocsList];
    if (order === "up") {
      const tmp = tmpGeolocs[index];
      tmpGeolocs[index] = tmpGeolocs[index - 1];
      tmpGeolocs[index - 1] = tmp;
    } else {
      const tmp = tmpGeolocs[index];
      tmpGeolocs[index] = tmpGeolocs[index + 1];
      tmpGeolocs[index + 1] = tmp;
    }
    setGeolocsList(tmpGeolocs);
    handleSaveGeolocs(tmpGeolocs);
  };

  const handleAddCurrentPosition = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const { latitude, longitude } = position.coords;
        const newGeoloc = {
          title: `Position (${latitude}, ${longitude})`,
          lat: latitude,
          long: longitude,
          radius: newMarker.radius || 100,
          visible: newMarker.visible,
          free: newMarker.free,
          controlDate: newMarker.controlDate,
          startDate: newMarker.startDate,
          endDate: newMarker.endDate,
          guid: guidGenerator(),
        };
        const updatedList = [...geolocsList, newGeoloc];
        setGeolocsList(updatedList);
        handleSaveGeolocs(updatedList);
      });
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewMarker({
      title: "",
      latitude: "",
      longitude: "",
      radius: 100,
      visible: false,
      free: false,
      controlDate: false,
      startDate: null,
      endDate: null,
    });
  };

  const handleAddMarker = () => {
    const newGeoloc = {
      title: newMarker.title,
      lat: newMarker.latitude,
      long: newMarker.longitude,
      radius: newMarker.radius,
      visible: newMarker.visible,
      free: newMarker.free,
      controlDate: newMarker.controlDate,
      startDate: newMarker.startDate,
      endDate: newMarker.endDate,
      guid: guidGenerator(),
    };
    const updatedList = [...geolocsList, newGeoloc];
    setGeolocsList(updatedList);
    handleSaveGeolocs(updatedList);
    handleCloseDialog();
  };

  const handleChangeMarkerStartDate = (index, date) => {
    const newGeolocsList = [...geolocsList];
    newGeolocsList[index].startDate = date;
    setGeolocsList(newGeolocsList);
    handleSaveGeolocs(newGeolocsList);
  };

  const handleChangeMarkerEndDate = (index, date) => {
    const newGeolocsList = [...geolocsList];
    newGeolocsList[index].endDate = date;
    setGeolocsList(newGeolocsList);
    handleSaveGeolocs(newGeolocsList);
  };

  const removeMarker = (index) => {
    setItemToDelete(index);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete !== null) {
      const newGeolocsList = [...geolocsList];
      newGeolocsList.splice(itemToDelete, 1);
      setGeolocsList(newGeolocsList);
      handleSaveGeolocs(newGeolocsList);
    }
    setOpenDeleteDialog(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
    setItemToDelete(null);
  };

  return (
    <GeolocMapListContainer>
      <Box sx={{ marginLeft: width < 769 ? "0" : "20px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            marginBottom: "10px",
          }}
        >
          <Button variant="contained" onClick={handleAddCurrentPosition}>
            {t("myExpariences.geolocMapList.addCurrentPosition")}
          </Button>
          <IconButton color="primary" onClick={handleOpenDialog}>
            <AddLocationAltIcon />
          </IconButton>
        </Box>
        <Paper
          elevation={3}
          className={classes.geolocListContainer}
          sx={{ height: width < 769 ? "auto" : "400px" }}
        >
          <Box sx={{ height: "100%", backgroundColor: "transparent" }}>
            <List>
              {geolocsList.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ textAlign: "center" }}>
                        {t("myExpariences.geolocMapList.noGeolocs")}
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                geolocsList.map((item, index) => {
                  const isExpired = dayjs(item.endDate).isBefore(dayjs());
                  return (
                    <React.Fragment key={`Geoloc-${index}`}>
                      <ListItem>
                        <Box>
                          <ListItemText
                            primary={
                              <Paper
                                elevation={1}
                                sx={{
                                  padding: "10px",
                                  boxShadow: isExpired
                                    ? "0 0 10px #F08080"
                                    : "0 0 10px #77DD77",
                                  transition: "box-shadow 0.3s ease",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box className={classes.arrowsContainer}>
                                    <IconButton
                                      edge="end"
                                      aria-label="edit"
                                      onClick={() =>
                                        handleMarkerPosition(index, "up")
                                      }
                                      size="large"
                                    >
                                      <KeyboardArrowUpIcon />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      aria-label="locate"
                                      onClick={() =>
                                        handleMarkerPosition(index, "down")
                                      }
                                      size="large"
                                    >
                                      <KeyboardArrowDownIcon />
                                    </IconButton>
                                  </Box>
                                  <Box sx={{ width: "60%" }}>
                                    <TextField
                                      variant="outlined"
                                      margin="normal"
                                      fullWidth
                                      label={t("positions.input")}
                                      id={`textfield-${index}`}
                                      value={item.title}
                                      onChange={(e) =>
                                        handleChangeMarkerTitle(
                                          index,
                                          e.target.value
                                        )
                                      }
                                    />
                                    <TextField
                                      variant="outlined"
                                      margin="normal"
                                      fullWidth
                                      label={t("positions.inputRadius")}
                                      value={item.radius || 100}
                                      id={`textfield-radius-${index}`}
                                      name={`textfield-radius-${index}`}
                                      type="number"
                                      onChange={(e) =>
                                        handleChangeMarkerRadius(
                                          index,
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "space-around",
                                    }}
                                  >
                                    <IconButton
                                      aria-label="locate"
                                      onClick={() => handlePositionFocus(index)}
                                      size="large"
                                    >
                                      <MyLocationIcon />
                                    </IconButton>
                                    <IconButton
                                      aria-label="delete"
                                      onClick={() => removeMarker(index)}
                                      size="large"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-around",
                                    marginBottom: item.controlDate
                                      ? "20px"
                                      : "0",
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={item.visible}
                                        onChange={() =>
                                          handleCheckboxChange(index, "visible")
                                        }
                                      />
                                    }
                                    label={t(
                                      "myExpariences.geolocMapList.visible"
                                    )}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={item.free}
                                        onChange={() =>
                                          handleCheckboxChange(index, "free")
                                        }
                                      />
                                    }
                                    label={t(
                                      "myExpariences.geolocMapList.free"
                                    )}
                                  />
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={item.controlDate}
                                        onChange={() =>
                                          handleCheckboxChange(
                                            index,
                                            "controlDate"
                                          )
                                        }
                                      />
                                    }
                                    label={t(
                                      "myExpariences.geolocMapList.controlDate"
                                    )}
                                  />
                                </Box>
                                {item.controlDate && (
                                  <Box sx={{ display: "flex", gap: "10px" }}>
                                    <LocalizationProvider
                                      dateAdapter={AdapterDayjs}
                                    >
                                      <DatePicker
                                        format="DD/MM/YYYY"
                                        label={t(
                                          "myExpariences.geolocMapList.startDate"
                                        )}
                                        value={
                                          item.startDate
                                            ? dayjs(item.startDate)
                                            : null
                                        }
                                        onChange={(date) =>
                                          handleChangeMarkerStartDate(
                                            index,
                                            date
                                          )
                                        }
                                      />
                                      <DatePicker
                                        format="DD/MM/YYYY"
                                        label={t(
                                          "myExpariences.geolocMapList.endDate"
                                        )}
                                        value={
                                          item.endDate
                                            ? dayjs(item.endDate)
                                            : null
                                        }
                                        onChange={(date) =>
                                          handleChangeMarkerEndDate(index, date)
                                        }
                                      />
                                    </LocalizationProvider>
                                  </Box>
                                )}
                              </Paper>
                            }
                          />
                        </Box>
                      </ListItem>
                    </React.Fragment>
                  );
                })
              )}
            </List>
            {width < 769 && geolocsList.length > 3 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  margin: "10px 0",
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll
                    ? t("myExpariences.geolocMapList.seeLess")
                    : t("myExpariences.geolocMapList.seeMore")}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{t("myExpariences.geolocMapList.addMarker")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("myExpariences.geolocMapList.title")}
            fullWidth
            variant="outlined"
            value={newMarker.title}
            onChange={(e) =>
              setNewMarker({ ...newMarker, title: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t("Latitude")}
            type="number"
            fullWidth
            variant="outlined"
            value={newMarker.latitude}
            onChange={(e) =>
              setNewMarker({ ...newMarker, latitude: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t("Longitude")}
            type="number"
            fullWidth
            variant="outlined"
            value={newMarker.longitude}
            onChange={(e) =>
              setNewMarker({ ...newMarker, longitude: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t("myExpariences.geolocMapList.radius")}
            type="number"
            fullWidth
            variant="outlined"
            value={newMarker.radius}
            onChange={(e) =>
              setNewMarker({ ...newMarker, radius: e.target.value })
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newMarker.visible}
                onChange={(e) =>
                  setNewMarker({ ...newMarker, visible: e.target.checked })
                }
              />
            }
            label={t("myExpariences.geolocMapList.visible")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newMarker.free}
                onChange={(e) =>
                  setNewMarker({ ...newMarker, free: e.target.checked })
                }
              />
            }
            label={t("myExpariences.geolocMapList.free")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={newMarker.controlDate}
                onChange={(e) =>
                  setNewMarker({ ...newMarker, controlDate: e.target.checked })
                }
              />
            }
            label={t("myExpariences.geolocMapList.controlDate")}
          />
          {newMarker.controlDate && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t("myExpariences.geolocMapList.startDate")}
                  value={
                    newMarker.startDate ? dayjs(newMarker.startDate) : null
                  }
                  onChange={(date) =>
                    setNewMarker({ ...newMarker, startDate: date })
                  }
                />
                <DatePicker
                  label={t("myExpariences.geolocMapList.endDate")}
                  value={newMarker.endDate ? dayjs(newMarker.endDate) : null}
                  onChange={(date) =>
                    setNewMarker({ ...newMarker, endDate: date })
                  }
                />
              </LocalizationProvider>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            color="primary"
          >
            {t("myExpariences.geolocMapList.cancelMarker")}
          </Button>
          <Button variant="contained" onClick={handleAddMarker} color="primary">
            {t("myExpariences.geolocMapList.addMarker")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDeleteDialog} onClose={handleCancelDelete}>
        <DialogTitle>{t("purchaseGeoloc.confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>{t("purchaseGeoloc.confirmDeleteMessage")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleCancelDelete}
            color="primary"
          >
            {t("purchaseGeoloc.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            color="error"
          >
            {t("purchaseGeoloc.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </GeolocMapListContainer>
  );
};

export default GeolocMapList;
